import { handleKeyDown } from "@faire/deprecated-retailer-visitor-shared/lib/handleKeyDown";
// eslint-disable-next-line import/no-unassigned-import
import "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/animation.tw.css";
import { useAnimatedScroll } from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/__internal__/Animation/useAnimatedScroll";
import { BrandValueBannerBrandInfo } from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/BrandValueBanner/__internal__/BrandValueBannerBrandInfo";
import { useGetBrandValues } from "@faire/deprecated-retailer-visitor-shared/views/home/logged-out-homepage/__internal__/components/BrandValueBanner/__internal__/useGetBrandValues";
import { Core } from "@faire/design-tokens";
import { setTimeoutIfPossible } from "@faire/next-utils/isomorphic/setTimeoutIfPossible";
import { trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick } from "@faire/web-api--source/events/loggedOutRetailers/click/loggedOutHomeRedesignCta";
import { trackLoggedOutRetailersLoggedOutHomeRedesignView } from "@faire/web-api--source/events/loggedOutRetailers/view/loggedOutHomeRedesign";
import { isPrerender } from "@faire/web/common/user-agent/isPrerender";
import { Column } from "@faire/web/slate/Layout";
import { Typography } from "@faire/web/slate/Typography";
import { cn } from "@faire/web/tools/cn";
import { useDoActionWhenVisible } from "@faire/web/ui/hooks/useDoActionWhenVisible";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import React from "react";

export const BrandValueBanner: React.FC = () => {
  const getBrandValues = useGetBrandValues();
  const ref = useDoActionWhenVisible(() =>
    trackLoggedOutRetailersLoggedOutHomeRedesignView("brand_value_banner", "v1")
  );
  const {
    resumeScroll,
    pauseScroll,
    isVisible,
    currentIndex,
    setCurrentIndex,
  } = useAnimatedScroll(getBrandValues?.length ?? 0, 5000);

  const handleClickTab = (index: number, brandValue: string) => {
    pauseScroll();
    setCurrentIndex(index);
    trackLoggedOutRetailersLoggedOutHomeRedesignCtaClick(
      "brand_value_banner-tab",
      brandValue,
      "v1"
    );
    setTimeoutIfPossible(() => {
      resumeScroll();
    }, 12000);
  };

  return (
    <Column
      gap="2x"
      className={cn("mx-8 my-12 items-start lg:my-[90px] lg:items-center")}
      ref={ref}
    >
      <Typography
        variant={{
          mobileAndAbove: "displaySSerifRegular",
          desktopAndAbove: "displayMSerifRegular",
        }}
      >
        <StrictLocalMsg
          id="GeVLCg"
          defaultMessage="Shop by the values you care most about"
          description={{
            text: "brand value banner header",
            img: "https://cdn.faire.com/fastly/1bb1746876e68497e743ddaa9806c3a4bf9b152cc5023da2ca04ec028d61f734.png",
          }}
        />
      </Typography>

      <div
        className="flex w-full flex-wrap justify-start gap-2 py-2 lg:max-w-[866px] lg:justify-center"
        style={{ maxWidth: "866px" }}
      >
        {getBrandValues.map((brandValue, index) => (
          <div
            key={index}
            tabIndex={0}
            onClick={() => handleClickTab(index, brandValue.id)}
            onKeyDown={handleKeyDown(() =>
              handleClickTab(index, brandValue.id)
            )}
            className={cn(
              "flex h-[40px] cursor-pointer items-center justify-center rounded-[40px] border p-4",
              index === currentIndex
                ? "bg-core-surface-default border-core-border-default"
                : "border-core-border-muted hover:border-core-border-default focus-visible:border-core-border-default bg-transparent"
            )}
          >
            <Typography
              color={
                index === currentIndex ? Core.text.inverse : Core.text.default
              }
            >
              {brandValue.name}
            </Typography>
          </div>
        ))}
      </div>
      <div
        style={{ overflow: "hidden" }}
        onMouseEnter={pauseScroll}
        onMouseLeave={resumeScroll}
        className={cn(
          "relative items-start lg:items-center",
          isVisible
            ? "animate-fade-in bottom-0 opacity-100"
            : "animate-fade-out bottom-[-40px] opacity-0"
        )}
        data-is-visible={isVisible}
      >
        {isPrerender() ? (
          <>
            {/* we want to prerender all brand values */}
            {getBrandValues.map((brandValue, index) => (
              <BrandValueBannerBrandInfo
                key={index}
                brandInfo={brandValue.brand}
                className={index ? "invisible h-0" : ""}
              />
            ))}
          </>
        ) : (
          <BrandValueBannerBrandInfo
            brandInfo={getBrandValues[currentIndex]?.brand}
          />
        )}
      </div>
    </Column>
  );
};
